import {
  FileType,
  ProjectDocument,
  ProjectPermission,
  useProjectDocumentsService
} from 'nekst-api';
import { AfterSubmitFunc, CheckboxInput, Dropdown, FormTemplate, useListContext } from 'ui-builder';
import { FileUploaderInput, usePopupFormManager } from 'features/nekst-widgets';
import React from 'react';
import { useAuthorizationChecker } from 'authorization-scope';

export function LabelsDropdown(props: {
  documents: ProjectDocument[],
}) {
  if (props.documents.length) {
    return (
      <Dropdown
        options={props.documents.map((item) => ({
          value: item.labelId!,
          label: item._label.name,
        }))}
        source="labelId"
        label="Document Label"
        placeholder="Without Label"
      />
    );
  } else {
    return null;
  }
}

export function CreateDocumentForm(props: {
  afterSubmitFunc?: AfterSubmitFunc,
  projectId: number,
  documentsWithoutFile: ProjectDocument[],
}) {
  const service = useProjectDocumentsService();

  return (
    <FormTemplate<{ labelId?: number, fileId: number, addToClientPortal?: boolean }>
      afterSubmitFunc={props.afterSubmitFunc}
      title="Upload Document"
      submitFormFunc={async (data) => {
        if (data.labelId) {
          const document = props.documentsWithoutFile
            .find((doc) => doc.labelId === data.labelId);

          if (document) {
            await service.setFile(document.id, data.fileId);
            await service.addToPortal(
              props.projectId,
              [document.id]
            );
          }
        } else {
          await service.createDocument(props.projectId, data.fileId, data.addToClientPortal);
        }
        return data;
      }}
      validationSchema={{
        fileId: {
          type: 'int',
          constraints: {
            required: true
          }
        }
      }}
    >
      <FileUploaderInput
        fileType={FileType.PROJECT_DOCUMENT}
        fileDataSource="_file"
        source="fileId"
        label="File"
      />
      <LabelsDropdown documents={props.documentsWithoutFile} />
      <CheckboxInput source="addToClientPortal" label="Add to Client Portal" />
    </FormTemplate>
  );
}

export function useCreateDocumentForm() {
  const listContext = useListContext<ProjectDocument>();

  const { isGranted } = useAuthorizationChecker();

  const documentsWithoutFile = listContext.data!.filter(
    (item) => item.labelId && !item.fileId,
  );

  const popupFormManager = usePopupFormManager();

  const openForm = (projectId: number) => {
    popupFormManager.openForm(
      <CreateDocumentForm
        documentsWithoutFile={documentsWithoutFile}
        projectId={projectId}
      />,
    );
  };

  return {
    documentsWithoutFile,
    isGranted: () => isGranted(ProjectPermission.CREATE_DOCUMENTS),
    openForm,
  };
}

